import React, { useCallback, useContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { ArrowLeft, Download, Loader2, ShieldCheck } from 'lucide-react'
import UserContext from '../../../../contexts/UserContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import {
  BackButton,
  Body,
  Card,
  FieldGroup,
  FieldsGrid,
  HeaderActions,
  HeaderApprovalName,
  HeaderProductName,
  HeaderRef,
  HeaderSubtitle,
  HeaderTitle,
  HeaderTitleRow,
  Input,
  Label,
  ModalHeader,
  ModalShell,
  OutlineButton,
  Overlay,
  PrimaryButton,
  SectionTitle,
  StatusBadge,
} from './styles'
import { ApprovalApi } from './api'
import {
  ChecklistItem,
  ProductApprovalDetail,
  ProductApprovalStatus,
} from './types'
import ImagesSection from './ImagesSection'
import ChecklistSection from './ChecklistSection'

interface Props {
  approvalId: number
  onClose: () => void
  onChanged?: () => void
}

const statusLabel = (status: ProductApprovalStatus) =>
  status === 'IN_PROGRESS'
    ? 'Em andamento'
    : status === 'APPROVED'
      ? 'Aprovado'
      : 'Reprovado'

const toDateInputValue = (iso: string | null) => {
  if (!iso) return ''
  return iso.slice(0, 10)
}

const toIso = (date: string) => {
  if (!date) return null
  return new Date(`${date}T00:00:00`).toISOString()
}

const ApprovationModal: React.FC<Props> = ({ approvalId, onClose, onChanged }) => {
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)
  const isAdmin = user?.role === 'ADMIN_GLOBAL' || user?.role === 'TENANT_ADMIN'
  const currentUserId = user?.sub

  const [approval, setApproval] = useState<ProductApprovalDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingField, setSavingField] = useState(false)
  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('')
  const [sizesInput, setSizesInput] = useState('')

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const data = await ApprovalApi.getById(approvalId)
      setApproval(data)
      setName(data.name)
      setStartDate(toDateInputValue(data.startDate))
      setExpectedDeliveryDate(toDateInputValue(data.expectedDeliveryDate))
      setSizesInput((data.sizes ?? []).join(', '))
    } catch (err) {
      setFeedback({
        isOpen: true,
        message: 'Erro ao carregar aprovação.',
        statusOk: false,
      })
    } finally {
      setLoading(false)
    }
  }, [approvalId, setFeedback])

  useEffect(() => {
    reload()
  }, [approvalId, reload])

  const locked = !!approval?.lockedAt

  const persistField = async (
    payload: Parameters<typeof ApprovalApi.update>[1],
  ) => {
    if (!approval || locked) return
    setSavingField(true)
    try {
      await ApprovalApi.update(approval.id, payload)
      onChanged?.()
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao salvar.',
        statusOk: false,
      })
    } finally {
      setSavingField(false)
    }
  }

  const handleNameBlur = async () => {
    if (!approval) return
    if (name.trim() && name !== approval.name) {
      await persistField({ name: name.trim() })
      setApproval({ ...approval, name: name.trim() })
    } else {
      setName(approval.name)
    }
  }

  const handleStartDateChange = async (value: string) => {
    setStartDate(value)
    if (!approval) return
    await persistField({ startDate: toIso(value) })
    setApproval({ ...approval, startDate: toIso(value) })
  }

  const handleExpectedDateChange = async (value: string) => {
    setExpectedDeliveryDate(value)
    if (!approval) return
    await persistField({ expectedDeliveryDate: toIso(value) })
    setApproval({ ...approval, expectedDeliveryDate: toIso(value) })
  }

  const handleSizesBlur = async () => {
    if (!approval) return
    const parsed = sizesInput
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (JSON.stringify(parsed) === JSON.stringify(approval.sizes ?? [])) return
    await persistField({ sizes: parsed })
    setApproval({ ...approval, sizes: parsed })
  }

  const handleUploadImage = async (slot: 'front' | 'side' | 'back', file: File) => {
    if (!approval || locked) return
    try {
      const uploaded = await ApprovalApi.uploadFile(file)
      const fieldKey =
        slot === 'front' ? 'frontImageId' : slot === 'side' ? 'sideImageId' : 'backImageId'
      const previewKey = slot === 'front' ? 'FrontImage' : slot === 'side' ? 'SideImage' : 'BackImage'
      await ApprovalApi.update(approval.id, { [fieldKey]: uploaded.id })
      setApproval({
        ...approval,
        [fieldKey]: uploaded.id,
        [previewKey]: uploaded,
      } as ProductApprovalDetail)
      onChanged?.()
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao enviar imagem.',
        statusOk: false,
      })
    }
  }

  const handleItemsChanged = (items: ChecklistItem[]) => {
    if (!approval) return
    setApproval({ ...approval, ChecklistItems: items })
    onChanged?.()
    // refresh status from backend (since decision endpoint updates parent status/lockedAt)
    ApprovalApi.getById(approval.id).then((fresh) => {
      setApproval(fresh)
    })
  }

  const handlePromote = async () => {
    if (!approval) return
    if (!window.confirm('Promover este piloto a Mostruário? O registro ficará bloqueado para edição.')) {
      return
    }
    try {
      await ApprovalApi.promote(approval.id)
      await reload()
      onChanged?.()
      setFeedback({
        isOpen: true,
        message: 'Piloto promovido a mostruário com sucesso.',
        statusOk: true,
      })
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Não foi possível promover.',
        statusOk: false,
      })
    }
  }

  const allItemsApproved =
    !!approval &&
    approval.ChecklistItems.length > 0 &&
    approval.ChecklistItems.every((i) => i.status === 'APPROVED')

  const showPromote =
    !!approval &&
    !locked &&
    approval.type === 'PILOT' &&
    allItemsApproved &&
    isAdmin

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <ModalShell onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <BackButton type='button' onClick={onClose}>
            <ArrowLeft size={16} /> Ficha Técnica
          </BackButton>
          <HeaderTitle>
            <HeaderTitleRow>
              <HeaderProductName>{approval?.Product?.name ?? '...'}</HeaderProductName>
              <HeaderApprovalName>{approval?.name ?? ''}</HeaderApprovalName>
              {approval && <StatusBadge $status={approval.status}>{statusLabel(approval.status)}</StatusBadge>}
              {approval && (
                <HeaderRef>
                  #{approval.id}
                </HeaderRef>
              )}
            </HeaderTitleRow>
            {approval && (
              <HeaderSubtitle>
                Ref. {approval.Product?.productCode ?? '—'} ·{' '}
                {approval.type === 'PILOT' ? 'Piloto' : 'Mostruário'} ·{' '}
                {approval.ChecklistItems.filter((i) => i.status === 'APPROVED').length}/
                {approval.ChecklistItems.length} itens aprovados
              </HeaderSubtitle>
            )}
          </HeaderTitle>
          <HeaderActions>
            <OutlineButton type='button' disabled title='Em breve'>
              <Download size={14} /> Exportar PDF
            </OutlineButton>
            {showPromote && (
              <PrimaryButton type='button' onClick={handlePromote}>
                <ShieldCheck size={14} /> Promover a Mostruário
              </PrimaryButton>
            )}
          </HeaderActions>
        </ModalHeader>

        <Body>
          {loading || !approval ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 60,
                color: '#6b7280',
              }}
            >
              <Loader2 size={24} className='spin' />
              <span style={{ marginLeft: 10, fontSize: 13 }}>Carregando aprovação...</span>
            </div>
          ) : (
            <>
              <Card>
                <SectionTitle>Dados da aprovação</SectionTitle>
                <FieldsGrid>
                  <FieldGroup>
                    <Label>Nome</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onBlur={handleNameBlur}
                      disabled={locked || savingField}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Tipo</Label>
                    <Input value={approval.type === 'PILOT' ? 'Piloto' : 'Mostruário'} disabled />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Data início</Label>
                    <Input
                      type='date'
                      value={startDate}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      disabled={locked || savingField}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Entrega prevista</Label>
                    <Input
                      type='date'
                      value={expectedDeliveryDate}
                      onChange={(e) => handleExpectedDateChange(e.target.value)}
                      disabled={locked || savingField}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Tamanhos (separados por vírgula)</Label>
                    <Input
                      value={sizesInput}
                      onChange={(e) => setSizesInput(e.target.value)}
                      onBlur={handleSizesBlur}
                      disabled={locked || savingField}
                      placeholder='Ex.: 38, 40, 42'
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Fornecedor</Label>
                    <Input
                      value={approval.SupplierCompany?.companyName ?? '—'}
                      disabled
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Criado por</Label>
                    <Input value={approval.CreatedBy?.name ?? '—'} disabled />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Aprovador</Label>
                    <Input value={approval.Approver?.name ?? '—'} disabled />
                  </FieldGroup>
                </FieldsGrid>
              </Card>

              <ImagesSection
                front={approval.FrontImage}
                side={approval.SideImage}
                back={approval.BackImage}
                onUpload={handleUploadImage}
                disabled={locked}
              />

              <ChecklistSection
                approvalId={approval.id}
                items={approval.ChecklistItems}
                isAdmin={isAdmin}
                currentUserId={currentUserId}
                locked={locked}
                onItemsChanged={handleItemsChanged}
              />
            </>
          )}
        </Body>
      </ModalShell>
    </Overlay>,
    document.body,
  )
}

export default ApprovationModal
